"use client";

import { PlugIcon } from "lucide-react";

import {
  SOURCE_DISC_CLASS,
  SourceDiscMark,
  TwinDiscMark,
} from "@/components/app/runs/run-sources-used-view";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

const SHOWN = 4;

/** What the sources row reads of a connected source (a ConnectionProviderStatus). */
export type ComposerSource = {
  id: string;
  label: string;
  pluginSlugs?: readonly string[];
  iconUrl?: string;
};

/** "GitHub", "GitHub and SUN", "GitHub, SUN and Gmail". */
export function sourceNames(sources: readonly { label: string }[]): string {
  const names = sources.map((source) => source.label);
  if (names.length <= 1) return names[0] ?? "";
  return `${names.slice(0, -1).join(", ")} and ${names.at(-1)}`;
}

/*
 * What your Twin can reach from a prompt, drawn like an answer's "Retrieved
 * with" line: the Twin itself first, always, then the connected sources, each
 * a small overlapping disc that opens the Twin's Marketplace. The Twin's mark
 * works for five seconds as the prompt appears, then rests. With nothing
 * connected, an offer to connect a source follows. The Twin picks the sources
 * each question needs, so this says "can use", never "will use".
 * ComposerSources renders it for the signed-in workspace.
 */
export function ComposerSourcesView({
  sources,
  offerConnect,
  onOpen,
}: {
  sources: readonly ComposerSource[];
  /** The connections have loaded and none is connected. */
  offerConnect: boolean;
  onOpen?: () => void;
}) {
  const names = sourceNames([{ label: "your workspace" }, ...sources]);
  return (
    <span className="flex items-center gap-1">
      <Tooltip>
        <TooltipTrigger
          render={
            <Button
              type="button"
              size="xs"
              variant="ghost"
              className="gap-1 px-1"
              aria-label={`Your Twin can use ${names}`}
              onClick={onOpen}
            />
          }
        >
          <span className="flex items-center -space-x-1.5">
            <span className={SOURCE_DISC_CLASS}>
              <TwinDiscMark durationSeconds={5} />
            </span>
            {sources.slice(0, SHOWN).map((provider) => (
              <span key={provider.id} className={SOURCE_DISC_CLASS}>
                <SourceDiscMark
                  plugin={{
                    slug: provider.pluginSlugs?.[0] ?? provider.id,
                    label: provider.label,
                    ...(provider.iconUrl ? { branding: { iconUrl: provider.iconUrl } } : {}),
                  }}
                />
              </span>
            ))}
          </span>
          {sources.length > SHOWN ? (
            <span className="text-2xs text-muted-foreground">+{sources.length - SHOWN}</span>
          ) : null}
        </TooltipTrigger>
        <TooltipContent sideOffset={6}>Can use {names}</TooltipContent>
      </Tooltip>
      {offerConnect ? (
        <Button
          type="button"
          size="xs"
          variant="ghost"
          className="font-normal text-muted-foreground"
          onClick={onOpen}
        >
          <PlugIcon data-icon="inline-start" />
          Connect a source
        </Button>
      ) : null}
    </span>
  );
}
